import React from 'react';

interface FlagRuleProps {
  /** Rule thickness. */
  weight?: 'hairline' | 'thin' | 'normal' | 'bold';
  /** Width and positioning, e.g. "w-16 mx-auto" or "w-full". */
  className?: string;
}

/*
 * The rule is one line now, so these are its actual height. They are the
 * totals the stacked version came to - 3 bands of 2, 3, 4 and 6px - so every
 * place that already uses it keeps the height it has and nothing on the page
 * moves.
 */
const WEIGHTS: Record<NonNullable<FlagRuleProps['weight']>, string> = {
  hairline: 'h-1.5',
  thin: 'h-[9px]',
  normal: 'h-3',
  bold: 'h-[18px]',
};

/**
 * Ugandan flag trim - one line, divided into black, yellow and red.
 *
 * Left to right in flag order, which is the sequence people recognise.
 *
 * This is the one sanctioned exception to the site's achromatic palette, and
 * it only stays neutral if used as designed: all three colours, together, as a
 * thin rule. A single colour pulled out as a surface or button would read as
 * the NRM (yellow) or the NUP (red), which is what the neutral palette exists
 * to prevent. The three together read as the national flag - no party uses all
 * three as its identity.
 *
 * Decorative only, so it is hidden from assistive technology; it carries no
 * information a screen reader needs.
 */
const FlagRule: React.FC<FlagRuleProps> = ({ weight = 'hairline', className = 'w-16' }) => {
  const height = WEIGHTS[weight];
  return (
    <div data-component-id="src/components/common/FlagRule.tsx:41:4" data-component-path="src/components/common/FlagRule.tsx" data-component-line="41" data-component-end-line="45" data-component-file="FlagRule.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex overflow-hidden ${height} ${className}`} aria-hidden="true">
      <div data-component-id="src/components/common/FlagRule.tsx:42:6" data-component-path="src/components/common/FlagRule.tsx" data-component-line="42" data-component-end-line="42" data-component-file="FlagRule.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1" style={{ backgroundColor: 'var(--color-flag-black)' }} />
      <div data-component-id="src/components/common/FlagRule.tsx:43:6" data-component-path="src/components/common/FlagRule.tsx" data-component-line="43" data-component-end-line="43" data-component-file="FlagRule.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1" style={{ backgroundColor: 'var(--color-flag-yellow)' }} />
      <div data-component-id="src/components/common/FlagRule.tsx:44:6" data-component-path="src/components/common/FlagRule.tsx" data-component-line="44" data-component-end-line="44" data-component-file="FlagRule.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1" style={{ backgroundColor: 'var(--color-flag-red)' }} />
    </div>
  );
};

export default FlagRule;
